import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, switchMap } from 'rxjs/operators';
import { Observable, ObservableInput, throwError, of } from 'rxjs';
import { BlogPostType, BlogPostInput, BlogPosts } from 'src/app/models/blog';
import { environment } from 'src/environments/environment';
import { LocaleService } from 'src/app/services/locale.service';

@Injectable({
  providedIn: 'root'
})
export class BlogService {
  readonly fields = 'id,slug,type,date,modified,title.rendered,content.rendered,excerpt.rendered,link';

  constructor(
    private http: HttpClient,
    private localeService: LocaleService,
  ) { }

  get(data: BlogPostInput): Observable<BlogPosts> {
    const type = data.type || BlogPostType.Post;
    const params = {};

    params['limit'] = data.limit || 10;
    params['fields'] = this.fields;

    if (data.id) { params['id'] = data.id }
    if (data.slug) {
      if (data.slugLocale) {
        params['slug'] = `${data.slug}-${this.localeService.currentLocale}`;
      } else {
        params['slug'] = data.slug;
      }
    }
    if (data.search) { params['search'] = data.search }
    if (data.categories) { params['categories'] = data.categories.join(',') }
    if (data.limit) { params['per_page'] = data.limit.toString() }
    if (data.offset) { params['offset'] = data.offset.toString() }
    if (data.page) { params['page'] = data.page.toString() }
    if (data.order) { params['order'] = data.order }
    if (data.orderBy) { params['orderBy'] = data.orderBy }

    return this.http.get(`${environment.blog.api}/${type}s`, {
      params,
      observe: 'response'
    }).pipe(
      switchMap<HttpResponse<any[]>, ObservableInput<any>>(response => {
        if (!response || !response.body || response.body.length < 1) {
          return throwError({
            errors: [
              {
                message: 'Page Not Found',
                category: 'POST_NOT_FOUND'
              }
            ]
          });
        } else {
          return of(response);
        }
      }),
      map<HttpResponse<any[]>, BlogPosts>(response => ({
        count: params['limit'],
        page: params['page'] || 1, 
        totalCount: parseInt(response.headers.get('X-WP-Total')),
        pages: parseInt(response.headers.get('X-WP-TotalPages')),
        posts: response.body.map(item => ({
          id: item.id,
          type: item.type,
          slug: item.slug,
          title: item.title ? item.title.rendered : null,
          content: item.content ? item.content.rendered : null,
          excerpt: item.excerpt ? item.excerpt.rendered : null,
          link: item.link,
          createdAt: new Date(item.date),
          updatedAt: new Date(item.modified),
        }))
      })
    ))
  }

  getOne(data: BlogPostInput) {
    return this.get({...data, limit: 1, page: 1, offset: 0}).pipe(
      map(response => response ? response.posts[0] : null)
    );
  }
}
